import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    MenuItem,
    Chip,
    Select,
    InputLabel,
    FormControl,
    Box,
    Grid,
    SelectChangeEvent,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Autocomplete,
    Typography,
    InputAdornment,
    CircularProgress,
    useTheme,
} from "@mui/material";

import AdminApi from "Service/Admin.api";
import { StyledTableCell } from "../AdminList/ProfessionalUserList";
import StatusApi from "Service/Status.api";
import ColorData from "Components/CstmTheme/ColorData";
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from "@mui/icons-material/Person";
import DeleteIcon from '@mui/icons-material/Delete';
import { setAlertData, startLoading, stopLoading } from '../../Redux/Screen/Action';
import { useDispatch, useSelector } from "react-redux";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';


/* ------------------------------
   INTERFACES
------------------------------ */

interface CaseItem {
  pkCase: number;
  caseNumber: string;
  description: string;
  casePrice: number;
  client: {
    firstName: string;
    lastName: string;
    email: string;
    contactNumber: string;
  };
  currentProcessingStatus?: {
    pkProcessingStatus: number;
    statusName: string;
  } | null;
  currentProcessingStatusId?: number | null; // Add this
  assignedProfessional?: {
    firstName: string;
    lastName: string;
    contactNumber: string;
    id: number;
  } | null;
  assignedAdmin?: any | null;
  caseType: {
    caseTypeName: string;
    pkCaseType: number;
  };
  orderType: {
    pkOrderType?: number;
    orderTypeName: string;
    orderPrice: number;
  };
  verified?: boolean;
}


/* ------------------------------
   MAIN COMPONENT
------------------------------ */

const CaseDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { myInfo } = useSelector((state: any) => state?.user);
    const [originalCaseList, setOriginalCaseList] = useState([]);
    const [caseList, setCaseList] = useState<CaseItem[]>([]);
    const [adminList, setAdminList] = useState([]);
    const [verifyProfessionalList, setVerifyProfessionalList] = useState([]);
    const [editId, setEditId] = useState<number | null>(null);
    const [selectedStatusId, setSelectedStatusId] = useState<number | "">("");
    const [caseStatus, setCaseStatus] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const theme = useTheme();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useDispatch();
    const [isCaseEditMode, setIsCaseEditMode] = useState(false);
    const [selectedProId, setSelectedProId] = useState("");

    const [formData, setFormData] = useState({
        // ... existing fields ...
        assignedAdminId: "" as string | number,
        assignedProfessionalId: "" as string | number,
        verified: true,
        caseName: "" // for read-only display
    });

    useEffect(() => {
        const initLoad = async () => {
            await loadCases();
            await fetchVerifyProfessionalList();
            await fetchAdmins();
        };
        initLoad();
    }, []); // Empty array means this runs ONLY once on page load

    useEffect(() => {
        loadCases();
    }, [page, pageSize, searchQuery, selectedProId]);

    /* ----------------------------------
       Fetch Admin List
    ---------------------------------- */
    const loadCases = async () => {
        try {
            dispatch(startLoading({ loading: true, loadtext: "Fetching cases..." }));
            const res = await AdminApi.getAllCasesWithAssignedAdminProfessional(page, pageSize, searchQuery, selectedProId ? Number(selectedProId) : undefined);
            setCaseList(res.data.data);
            setTotalCount(res.data.totalCount);
        } catch (error) {
            console.error("Error fetching cases:", error);
        } finally {
            dispatch(stopLoading());
        }
    };

    const handleDeleteCase = async (caseId: number) => {
        if (!window.confirm("Are you sure you want to delete this case? This action cannot be undone.")) {
            return;
        }

        try {
            dispatch(startLoading({ loading: true, loadtext: "Deleting case..." }));
            const res = await AdminApi.deleteCase(caseId);
            if (res && res.success) {
                dispatch(setAlertData({
                    openAlert: true,
                    alertVariant: "success",
                    alertMessage: "Case deleted successfully!"
                }));
                loadCases(); // Refresh list
            } else {
                dispatch(setAlertData({
                    openAlert: true,
                    alertVariant: "error",
                    alertMessage: res?.message || "Failed to delete case."
                }));
            }
        } catch (error: any) {
            console.error("Error deleting case:", error);
            dispatch(setAlertData({
                openAlert: true,
                alertVariant: "error",
                alertMessage: error.response?.data || "An error occurred while deleting case."
            }));
        } finally {
            dispatch(stopLoading());
        }
    };

    /* ----------------------------------
       Fetch Order Type List
    ---------------------------------- */
    const fetchAdmins = async (orderTypeId?: number) => {
        try {
            const res = await AdminApi.getAllAdminUsers(orderTypeId);
            setAdminList(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error("Error fetching admins:", error);
        }
    };

    const fetchVerifyProfessionalList = async () => {
        try {
            const res = await AdminApi.GetAllActiveProfessionalUser();
            setVerifyProfessionalList(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error("Error fetching verify professional users:", error);
        }
    };

const handleCaseEdit = async (item: CaseItem) => {
    dispatch(startLoading({ loading: true, loadtext: "Loading Filtered Admins..." }));
    try {
        setEditId(item.pkCase); // Store the Case ID
        const res = await StatusApi.caseStatus(item.caseType?.pkCaseType);
        setCaseStatus(res.data);
        setIsCaseEditMode(true);
        
        const orderTypeIdToFilter = item.orderType?.pkOrderType || (item.orderType as any)?.PkOrderType || (item as any)?.orderTypeId || (item as any)?.OrderTypeId;
        
        // Fetch only admins who have privileges for this case's OrderType
        if (orderTypeIdToFilter) {
            await fetchAdmins(Number(orderTypeIdToFilter));
        } else {
            await fetchAdmins();
        }

        // Set the status ID specifically for the status dropdown
        setSelectedStatusId(item.currentProcessingStatusId || "");
        
        setFormData({
            ...formData,
            // Pre-fill labels for display
            caseName: item.caseType?.caseTypeName || "N/A",
            
            // Handle IDs: if item has assignedAdmin, use its id, else empty string
            assignedAdminId: item?.assignedAdmin?.id || "",
            assignedProfessionalId: item?.assignedProfessional?.id || "",
            
            // Map boolean verified to a string value for a standard Dropdown
            verified: item.verified ?? false, 
        });

        // Smooth scroll to top so the admin sees the form immediately
        window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
        dispatch(stopLoading());
    }
};

const handleReset = () => {
    setIsCaseEditMode(false);
    setEditId(null);
    setSelectedStatusId(""); // Clear the status dropdown
    fetchAdmins(); // Reset admin list to default
    setFormData({
        assignedAdminId: "",
        assignedProfessionalId: "",
        verified: true,
        caseName: ""
    });
};


const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Prevent double submissions
    if (isSubmitting) return;

    try {
        setIsSubmitting(true);
        dispatch(startLoading({ loading: true, loadtext: "Assigning Admin & Updating Case..." }));
        if (isCaseEditMode && editId) {
            // PAYLOAD FOR UPDATING THE CASE
            const caseUpdatePayload = {
                pkCase: editId,
                assignedAdminId: Number(formData.assignedAdminId) || null,
                assignedProfessionalId: formData.assignedProfessionalId || null,
                // verified: formData.verified
                statusId: selectedStatusId, // Sending the selected status
            };


            // Call your specific Case API
            const response: any = await AdminApi.updateCaseAssignment(editId, caseUpdatePayload);
            if (response?.success !== false) {
                dispatch(
                    setAlertData({
                        alertMessage: "Case Updated Successfully",
                        alertVariant: "success",
                        openAlert: true,
                    })
                );
                handleReset(); 
                loadCases();
            } else {
                dispatch(
                    setAlertData({
                        alertMessage: response?.message || response?.data?.message || "Error Updating Case",
                        alertVariant: "error",
                        openAlert: true,
                    })
                );
            }
        } else {
            // ... Logic for creating/updating Admin User ...
        }

        // handleReset(); // Reset form and exit Edit Mode
        // loadCases();   // Refresh the table
    } catch (error: any) {
        console.error("Error during update:", error);
        const errMsg = error?.response?.data?.message || error?.data?.message || error?.message || "Error Updating Case";
        dispatch(
            setAlertData({
                alertMessage: errMsg,
                alertVariant: "error",
                openAlert: true,
            })
        );
    } finally {
        setIsSubmitting(false); // Always stop loading spinner
        dispatch(stopLoading());
    }
};

  const handleStatusChange = (event: SelectChangeEvent<number>) => {
    setSelectedStatusId(event.target.value as number);
  };

//     const handleStampChange = (value: StampListDetailsType | null) => {
//       setSelectedStamp(value);
//     };

//       const selectedStatusDetails = caseStatus.find(
//     (status) => status.pkProcessingStatus === selectedStatusId
//   );
    /* ----------------------------------
       RENDER
    ---------------------------------- */
    return (
        <Box sx={{
            py: 4,
            pb: 12,               // Added extra padding at bottom for better visibility
            display: "flex",
            flexDirection: "column",
            height: "100vh",      // LIMITS height to screen size
            overflowY: "auto",    // ALLOWS scrolling within this container
        }}>
            {/* 3. ADDED BACK BUTTON HERE */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate(-1)}
                    sx={{ fontWeight: 'bold' }}
                >
                    Back
                </Button>
            </Box>

            {/* Parent Container to handle centering */}


            {isCaseEditMode && (
    <Box
        sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            width: "100%",
            p: 2
        }}
    >
        <Box
            width="100%"
            maxWidth="900px"
            mx="auto"
            p={4}
            component={Paper}
            elevation={3}
            borderRadius={3}
        >
            <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
                Updating Case ID: {editId}
            </h2>

            <p style={{ textAlign: "center", color: "#666", marginTop: "-15px", marginBottom: "20px" }}>
                Type: <strong>{formData.caseName}</strong>
            </p>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    {/* 1. Admin Assignment Search/Dropdown */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                            options={Array.isArray(adminList) ? adminList : []}
                            getOptionLabel={(option: any) => `${option.firstName} ${option.lastName} (${option.email})`}
                            value={(Array.isArray(adminList) ? adminList : []).find((a: any) => Number(a.id) === Number(formData.assignedAdminId)) || null}
                            onChange={(_, newValue) => {
                                setFormData({ ...formData, assignedAdminId: newValue?.id || "" });
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Assign Admin" placeholder="Select Admin" fullWidth />
                            )}
                        />
                    </Grid>

                    {/* 2. Professional Search */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                            options={Array.isArray(verifyProfessionalList) ? verifyProfessionalList : []}
                            getOptionLabel={(option: any) => `${option.firstName} ${option.lastName}`}
                            renderOption={(props, option: any) => (
                                <li {...props} key={option.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', borderBottom: '1px solid #eee', padding: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                        <span style={{ fontWeight: 'bold' }}>
                                            {option.firstName} {option.lastName}
                                        </span>
                                        <span style={{ fontSize: '0.75rem', color: '#1976d2', fontWeight: 600 }}>
                                            {option.highestQualification}
                                        </span>
                                    </div>
                                    <div style={{ marginTop: '4px' }}>
                                        {option.skills?.map((skill: any) => (
                                            <div key={skill.pkSkillProfessionalMapping} style={{ fontSize: '0.7rem', color: '#555' }}>
                                                • {skill.skillName} ({skill.numberOfExperienceYears} yrs exp)
                                            </div>
                                        ))}
                                    </div>
                                </li>
                            )}
                            value={(Array.isArray(verifyProfessionalList) ? verifyProfessionalList : []).find((p: any) => p.id === formData.assignedProfessionalId) || null}
                            onChange={(_, newValue) => {
                                setFormData({ ...formData, assignedProfessionalId: newValue?.id || "" });
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Search Professional & Skills" fullWidth />
                            )}
                        />
                    </Grid>

                    {/* 3. Status Dropdown */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel id="status-select-label">Select Status</InputLabel>
                            <Select
                                labelId="status-select-label"
                                value={selectedStatusId}
                                label="Select Status"
                                onChange={handleStatusChange}
                                required
                            >
                                {caseStatus.map((status) => (
                                    <MenuItem key={status.pkProcessingStatus} value={status.pkProcessingStatus}>
                                        {status.statusName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Buttons */}
                    <Grid size={{ xs: 12 }}>
                        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                            <Button 
                                type="submit" 
                                variant="contained" 
                                color="success" 
                                fullWidth 
                                disabled={isSubmitting}
                                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{ fontWeight: 'bold' }}
                            >
                                {isSubmitting ? "Updating..." : "Update Case Assignment"}
                            </Button>
                            <Button type="button" variant="outlined" fullWidth onClick={handleReset}>
                                Cancel
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Box>
    </Box>
)}

            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{
                padding: 2,
                borderRadius: 2,
                backgroundColor: theme => ColorData.primary.main,
                color: theme => ColorData.text.secondary,
            }}>
                Case Dashboard
            </Typography>

            {/* Search and Actions */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    {/* <SearchCard elevation={2}>
                                <CardContent> */}
                    <TextField
                        fullWidth
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setPage(1); // Reset to first page
                        }}
                        placeholder="Search Cases..."
                        variant="outlined"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="primary" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main,
                                    color: 'white',
                                },
                            },
                        }}
                    />
                    {/* </CardContent>
                            </SearchCard> */}
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Box display="flex" gap={1} height="100%" alignItems="center">
                        <Chip
                            icon={<PersonIcon />}
                            label={`Total Cases: ${totalCount}`}
                            color="info"
                            variant="outlined"
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                height: { xs: 28, sm: 32 }
                            }}
                        />
                    </Box>
                </Grid>

                {/* Right Side: Professional Filter Dropdown */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel id="pro-filter-label">Filter by Professional</InputLabel>
                        <Select
                            labelId="pro-filter-label"
                            value={selectedProId}
                            label="Filter by Professional"
                            onChange={(e) => {
                                setSelectedProId(e.target.value);
                                setPage(1); // Reset to first page
                            }}
                            sx={{ borderRadius: 3, bgcolor: 'white' }}
                        >
                            <MenuItem value="">
                                <em>All Professionals</em>
                            </MenuItem>
                            {verifyProfessionalList?.map((pro: any) => (
                                <MenuItem key={pro.id} value={pro.id}>
                                    {pro.firstName} {pro.lastName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>



            <h2 style={{ marginTop: "40px", textAlign: "center", }}>Case Listing</h2>

            {/* LISTING TABLE */}
            <Paper sx={{ px: 2, py: 2, boxShadow: 'none' }}>
                <TableContainer
                    sx={{
                        maxHeight: 470,
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                    }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Case Id</StyledTableCell>
                                <StyledTableCell>Case & Order Type</StyledTableCell>
                                <StyledTableCell>Client Details</StyledTableCell>
                                <StyledTableCell>Assigned Prof.</StyledTableCell>
                                <StyledTableCell>Assigned Admin</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="center">Action</StyledTableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {caseList?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No Cases Found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                caseList?.map((caseItem, index) => (
                                    <TableRow key={caseItem.pkCase} hover>
                                        <TableCell>{caseItem.caseNumber ? caseItem.caseNumber : caseItem.pkCase}</TableCell>

                                        {/* CASE & ORDER TYPE */}
                                        <TableCell>
                                            <div style={{ fontWeight: 'bold' }}>{caseItem.caseType?.caseTypeName}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                                Order: {caseItem.orderType?.orderTypeName || 'N/A'}
                                            </div>
                                        </TableCell>

                                        {/* CLIENT DETAILS */}
                                        <TableCell>
                                            <div>{`${caseItem.client?.firstName} ${caseItem.client?.lastName}`}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#1976d2' }}>{caseItem.client?.email}</div>
                                            <div style={{ fontSize: '0.75rem' }}>{caseItem.client?.contactNumber}</div>
                                        </TableCell>

                                        {/* ASSIGNED PROFESSIONAL */}
                                        <TableCell>
                                            {caseItem.assignedProfessional ? (
                                                <>
                                                    <div>{`${caseItem.assignedProfessional.firstName} ${caseItem.assignedProfessional.lastName}`}</div>
                                                    <div style={{ fontSize: '0.7rem', color: '#666' }}>{caseItem.assignedProfessional.contactNumber}</div>
                                                </>
                                            ) : (
                                                <Chip label="Unassigned" size="small" variant="outlined" />
                                            )}
                                        </TableCell>

                                        {/* ASSIGNED ADMIN */}
                                        <TableCell>
                                            {caseItem.assignedAdmin ? (
                                                <div>{caseItem.assignedAdmin.firstName}</div>
                                            ) : (
                                                <span style={{ color: '#ccc', fontStyle: 'italic' }}>Pending</span>
                                            )}
                                        </TableCell>

                                        {/* VERIFICATION STATUS */}
                                        <TableCell>
                                            <Chip
                                                label={caseItem.currentProcessingStatus?.statusName}
                                                // color={caseItem.currentProcessingStatusId ? "success" : "error"}
                                                size="small"
                                            />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Box display="flex" gap={1} justifyContent="center" alignItems="center">
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color="primary" // Blue color for edit
                                                    onClick={() => handleCaseEdit(caseItem)}
                                                    sx={{ fontSize: '0.7rem' }}
                                                >
                                                    Edit
                                                </Button>
                                                {myInfo?.userType === "SUPERADMIN" && (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        color="error" // Red color for delete
                                                        startIcon={<DeleteIcon sx={{ width: 14, height: 14 }} />}
                                                        onClick={() => handleDeleteCase(caseItem.pkCase)}
                                                        sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {/* MUI Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 2, color: 'text.secondary' }}>
                        Total: {totalCount} items
                    </Typography>
                    <Pagination
                        count={Math.ceil(totalCount / pageSize)}
                        page={page}
                        onChange={(_e, value) => setPage(value)}
                        color="primary"
                        showFirstButton 
                        showLastButton
                    />
                </Box>
            </Paper>
        </Box>
    );
};

export default CaseDashboard;
